import { useState, useEffect } from "react";
import Table from "Components/Common/Table/Table";
import { usePatientContext } from "Context/Patient";
import {
  GetPaymentDetails,
  PaymentRefund,
} from "Services/API/Patients.service";
import { formatDate } from "Helpers/DateFormat";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ApiError } from "Components/Common/ApiError";
import { ReactComponent as CheckIcon } from "Assets/Check.svg";
import { ReactComponent as CrossIcon } from "Assets/Deactivate.svg";
import { ReactComponent as RefundIcon } from "Assets/Refund.svg";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { RefundModal } from "./RefundModal";
import { PaymentDetailsModal } from "./PaymentDetailsModal";
import { ReactComponent as DollarIcon } from "Assets/Dollar.svg";
import Decimal from "decimal.js";

import { ACTIVE_ROW_CSS, None } from "Components/Patients/EditViewPatient/Forms/DetailSection/DetailSection";
import "Components/Patients/EditViewPatient/Forms/FormCommonCss/Form.css";
import "./PaymentsTable.css";
import "Colors.css";

const TABLE_HEADINGS = [
  "Membership",
  "Date",
  "Total",
  // What the visit actually bought, straight in the row — it used to be
  // reachable only by opening the Details modal one invoice at a time.
  "Cart",
  "Status",
  "",
  "",
];

// Same ordering the Details modal uses: primary product, then add-ons, then
// adjustments, so a row reads the way the receipt does.
const CART_SORT_ORDER = { PRIMARY: 0, ADD_ON: 1 };
const sortCartItems = (cart) =>
  [...cart].sort(
    (a, b) =>
      (a.product ? CART_SORT_ORDER[a.product] ?? 2 : 2) -
      (b.product ? CART_SORT_ORDER[b.product] ?? 2 : 2)
  );

// Sponsor adjustments carry their money in adjustmentValue, everything else in
// price — same rule the Details modal applies.
const formatCartItemPrice = (item) => {
  const raw =
    item?.adjustmentType === "SPONSOR" ? item?.adjustmentValue : item?.price;
  if (raw == null) return "";
  const amount = new Decimal(Number(raw));
  // Whole dollars lose the ".00" — cents only earn their space when a price
  // actually has them, which keeps the column scannable.
  return `$${amount.isInteger() ? amount.toFixed(0) : amount.toFixed(2)}`;
};

export const paymentFormatter = (data) => {
  const formatRefunds=(data) => {
return data.map((item)=>{
  return {
    id: item.id,
    refundAmount: item.refundedAmount||"",
    date:formatDate(item?.createdAt, "MM-DD-YYYY") || "",
    time:formatDate(item?.createdAt, "HH:mm A") || "",
    remainingAmount:item?.remainingAmount || ""
  }
})
  }
  return data.map((list) => {
    return {
      id: list?.id,
      amount: list?.amount || "",
      balance: list?.balance || "",
      date: formatDate(list?.createdAt, "MM-DD-YYYY") || "",
      paid: list?.status,
      memberShip: list?.membership?.name || "-",
      refundAmount: list?.refundedAmount || "",
      refunds: formatRefunds(list?.refunds)||[],
      cart: list?.cart || []
    };
  });
};

export const PaymentsTable = ({ setActiveTabIndex }) => {
  const { getPatientData } = usePatientContext();
  const [patientPayments, setPatientPayments] = useState([]);
  const [apiError, setApiError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [refundModal, setRefundModal] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedPatientDetails, setSelectedPatientDetails] = useState({
    invoicelogId: "",
    memberShip: "",
    amount: "",
    refund: "",
    date: "",
    refunds:[],
    cart: []
  });

  const fetchPayments = () => {
    if (getPatientData) {
      const { id } = getPatientData() || {};
      if (id) {
        setLoading(true);
        // Pagination controls were removed from this tab — fetch everything so
        // older invoices don't become unreachable.
        GetPaymentDetails(id, 1, 200)
          .then((res) => {
            setLoading(false);
            setPatientPayments(paymentFormatter(res?.data));
            setHasNextPage(res?.pagination?.hasNext);
            setHasPreviousPage(res?.pagination?.hasPrevious);
          })
          .catch((err) => {
            setLoading(false);
            console.error("[GetPaymentDetails][API_ERROR} -", err);
            setApiError(
              errorFormatter(err?.response?.data?.errors || err?.message)
            );
          });
      }
    }
    return () => {
      setPatientPayments([]);
    };
  };
  useEffect(() => {
    fetchPayments();
  }, [getPatientData, pageNumber]);

  return (
    <>
      <div className="TableWrapper flex flex-col gap-4">
        {apiError && <ApiError error={apiError} setApiError={setApiError} />}

        <Table
          heading={<TableHeading />}
          body={
            !loading && patientPayments.length > 0 ? (
              <TableBody
                data={patientPayments}
                setRefundModal={setRefundModal}
                setDetailsModal={setDetailsModal}
                setSelectedPatientDetails={setSelectedPatientDetails}
              />
            ) : (
              !loading && <None block />
            )
          }
        />
        {loading && <Spinner />}
      </div>
      {refundModal && (
        <RefundModal
          amount={selectedPatientDetails.amount}
          cart={selectedPatientDetails.cart}
          isModalOpen={refundModal}
          isLoading={isBtnLoading}
          setRefundAmount={setRefundAmount}
          onRefund={() => {
            if (selectedPatientDetails.invoicelogId) {
              setIsBtnLoading(true);
              PaymentRefund(selectedPatientDetails.invoicelogId, {
                amount: refundAmount,
              })
                .then((res) => {
                  fetchPayments();
                  setIsBtnLoading(false);
                  setRefundModal(false);
                })
                .catch((err) => {
                  console.error(
                    "[PROVIDERS_TABLE][MoveToArchive][API_ERROR - ",
                    err
                  );
                  setIsBtnLoading(false);
                  setRefundModal(false);
                  setApiError(err?.response?.data?.errors || err?.message);
                });
            }
          }}
          onCancel={() => {
            setIsBtnLoading(false);
            setRefundModal(false);
          }}
        />
      )}
      {detailsModal && (
        <PaymentDetailsModal
          isModalOpen={detailsModal}
          paymentDetails={selectedPatientDetails}
          onCancel={() => {
            setDetailsModal(false);
          }}
        />
      )}
    </>
  );
};
const TableHeading = () => {
  return TABLE_HEADINGS.map((head, index) => (
    <th
      key={index}
      scope="col"
      className="bali_hai px-6 py-3 text-left text-sm font-medium"
    >
      {head}
    </th>
  ));
};
const TableBody = ({
  data,
  setRefundModal,
  setDetailsModal,
  setSelectedPatientDetails,
}) => {
  const TABLE_ROW = "px-6 py-4 whitespace-nowrap text-sm text-black";
  // Refund and Details both open a modal over the table; the highlight is what
  // says which invoice the modal belongs to once it closes.
  const [activeRow, setActiveRow] = useState(null);

  return (
    <>
      {" "}
      {data.map((invoice, index) => (
        <tr key={index} className={index === activeRow ? ACTIVE_ROW_CSS : undefined}>
          <td className={TABLE_ROW}>{invoice.memberShip}</td>
          <td className={TABLE_ROW}>{invoice.date}</td>
          <td className={TABLE_ROW}>
            {invoice.amount ? (
              <span className="flex items-center"><DollarIcon className="w-5 h-5 light_grayish_navy pointer-events-none" /> {invoice.amount}</span>
            ) : (
              <None />
            )}
          </td>
          {/* Price then name as one run of text — no alignment column, so the
              two always read as a single phrase per item. */}
          <td className="px-6 py-4 text-sm light_grayish_navy">
            {invoice.cart?.length ? (
              <div className="flex flex-col gap-0.5">
                {sortCartItems(invoice.cart).map((item, i) => (
                  <span key={item?.id ?? i}>
                    {formatCartItemPrice(item)} {item?.name}
                  </span>
                ))}
              </div>
            ) : (
              <None />
            )}
          </td>

          <td className={TABLE_ROW}>
            <span className="flex items-center gap-2">
              {invoice.paid === "Paid" ? (
                <CheckIcon className="h-4 w-4 green cursor-pointer" />
              ) : invoice.paid === "Refunded" ? (
                <RefundIcon className="h-4 w-4 green cursor-pointer" />
              ) : invoice.paid === "Failed" ? (
                <CrossIcon className="h-4 w-4 green cursor-pointer" />
              ) : (
                ""
              )}
              {invoice.paid}
            </span>
          </td>
          <td
            className={`cursor-pointer TABLE_ROW text-blue-chill`}
            onClick={() => {
              setActiveRow(index);
              setRefundModal(true);
              setSelectedPatientDetails({
                memberShip: invoice.memberShip,
                refund: invoice.refundAmount,
                amount: invoice.amount,
                date: invoice.date,
                invoicelogId: invoice.id,
                cart: invoice.cart,
              });
            }}
          >
            Refund
          </td>
          <td
            className={`cursor-pointer TABLE_ROW text-blue-chill`}
            onClick={() => {
              setActiveRow(index);
              setSelectedPatientDetails({
                memberShip: invoice.memberShip,
                refund: invoice.refundAmount,
                amount: invoice.amount,
                date: invoice.date,
                refunds:[...invoice?.refunds],
                cart: invoice?.cart || []
              });
              setDetailsModal(true);
            }}
          >
            Details
          </td>
        </tr>
      ))}
    </>
  );
};
